import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Zap } from 'lucide-react';
import { SEO_KEYWORDS, COLORS } from '../constants';
import { SCOOTERS } from '../data/scooters';
import AnimatedIcon from '../components/AnimatedIcon';
import Golf from '../components/Golf';
import { startCheckout } from "../utils/checkout";
import Button from '../components/Button';
import SectionHeading from '../components/SectionHeading';

import { useNavigate } from 'react-router-dom';

const HomePage = () => {
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();

  const toggleSpecs = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <>
    <Helmet>
 <link rel="canonical" href="https://lectricleisure.golf/" />
  <title>Lectric Leisure | Premium Electric Golf Scooters Australia</title>

  <meta
    name="description"
    content="Lectric Leisure: Premium electric golf scooters with Immediate Delivery across Australia. Stable, silent, and affordable with full local service and warranty support."
  />
  <meta name="keywords" content={SEO_KEYWORDS} />

  <meta property="og:title" content="Lectric Leisure – Electric Golf Scooters" />
  <meta property="og:description" content="Premium electric scooters designed specifically for golf mobility." />
  <meta property="og:image" content="/images/intro.jpg" />
  <meta property="og:type" content="website" />

  <meta name="twitter:card" content="summary_large_image" />
</Helmet>
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden" style={{ backgroundColor: COLORS.primaryBg }}>
        <motion.div 
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <img 
            src="images/intro.jpg"
            alt="intro/backgroundhome" 
            className="w-full h-full object-cover"
          />
          <div 
            className="absolute inset-0" 
            style={{ 
              background: `linear-gradient(to bottom, ${COLORS.secondaryBg}CC 0%, ${COLORS.secondaryBg}99 40%, ${COLORS.primaryBg} 100%)` 
            }}
          ></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </motion.div>

        <div className="relative z-10 container mx-auto px-6 text-center pt-20">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.span 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="inline-block px-6 py-2 rounded-full font-bold tracking-[0.2em] mb-8 border-2 shadow-lg backdrop-blur-md" 
              style={{ backgroundColor: `${COLORS.accent}44`, color: COLORS.accent, borderColor: COLORS.accent }}
            >
              THE FUTURE OF GOLF MOBILITY
            </motion.span>
	    <motion.div
  		initial={{ opacity: 0, y: 10 }}
  		animate={{ opacity: 1, y: 0 }}
  		transition={{ delay: 0.6 }}
  		className="text-sm md:text-base font-bold tracking-widest uppercase mb-6"
  		style={{ color: COLORS.lightSurface, opacity: 0.85 }}
	       >
 		 |<u> No longer limited to BINGO </u>|
	    </motion.div>
            <h1 className="text-6xl md:text-8xl font-bold text-[#F2EBDC] mb-8 font-serif leading-tight drop-shadow-2xl">
              Electrify<br/><span style={{ color: COLORS.accent }}>Your Game.</span>
            </h1>
            <p className="text-xl md:text-3xl text-[#F2EBDC] mb-4 max-w-3xl mx-auto leading-relaxed font-semibold drop-shadow-lg">
  A premium yet affordable electric scooter crafted for speed, stability, and silent power.
</p>

<motion.p
  initial={{ opacity: 0, y: 8 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.2 }}
  className="text-sm md:text-base font-bold tracking-widest uppercase mb-8 text-[#F2EBDC]"
>
  |<u>GOOD BIT OF KIT</u>| 
</motion.p>
	
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button variant="primary" onClick={() => navigate('/scooters')}>
                Explore the Fleet
              </Button>
              <Button variant="outline" className="text-[#F2EBDC] border-[#F2EBDC] hover:bg-white hover:text-[#1F332A] backdrop-blur-sm shadow-xl" onClick={() => navigate('/quote')}>
                Extras & Spares
              </Button>
            </div>
          </motion.div>
        </div>
        
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-[#F2EBDC] opacity-80"
        >
          <AnimatedIcon Icon={Zap} size={40} />
        </motion.div>
      </section>

      {/* Trust & Local Service Banner */}
      <section className="py-10 border-b border-[#E0D8C8]" style={{ backgroundColor: COLORS.secondaryBg }}>
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#C7A36A]/20">
                <Zap size={24} color={COLORS.accent} />
              </div>
              <div>
                <h4 className="text-[#F2EBDC] font-bold uppercase tracking-widest text-sm mb-1">Immediate Delivery</h4>
                <p className="text-[#F2EBDC]/60 text-xs">Ready for the fairway today</p>
              </div>
            </div>
            
            <div className="flex flex-col items-center gap-3 border-y md:border-y-0 md:border-x border-white/10 py-6 md:py-0">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#C7A36A]/20">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </motion.div>
              </div>
              <div>
                <h4 className="text-[#F2EBDC] font-bold uppercase tracking-widest text-sm mb-1">Local Service & Warranty</h4>
                <p className="text-[#F2EBDC]/60 text-xs">Full support based locally in Australia</p>
              </div>
            </div>

            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#C7A36A]/20">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div>
                <h4 className="text-[#F2EBDC] font-bold uppercase tracking-widest text-sm mb-1">Australian Owned</h4>
                <p className="text-[#F2EBDC]/60 text-xs">Supporting golfers across the country</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model Selection Grid */}
      <section className="py-24" style={{ backgroundColor: COLORS.lightSurface }}>
        <div className="container mx-auto px-6">
          <SectionHeading title="Choose Your Ride" subtitle="Three distinct models. One unforgettable experience." />
          
          <div className="grid md:grid-cols-3 gap-8 items-start">
             {SCOOTERS.map((scooter) => (
                <motion.div 
                  key={scooter.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-[2rem] overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer border border-[#E0D8C8] group"
                  onClick={() => toggleSpecs(scooter.id)}
                >
                  <div className="relative h-80 bg-[#F9F7F2] flex items-center justify-center overflow-visible">
  <img
    src={scooter.images?.[0] || scooter.image}
    alt={scooter.name}
    className="max-h-[100%] max-w-[90%] object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.15)] transition-transform duration-500 group-hover:scale-115"
  />

                    <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors"></div>
                    <div className="absolute bottom-4 left-4">
                       <h3
  className="text-4xl font-black text-white tracking-wider"
  style={{
    WebkitTextStroke: "1.6px #C7A36A",
    textShadow: `
      0 3px 6px rgba(31,51,42,0.7),
      0 8px 18px rgba(31,51,42,0.4)
    `,
  }}
>
  {scooter.name}
</h3>
                    </div>
                    <div className={`absolute top-4 right-4 bg-white/90 p-2 rounded-full transition-transform duration-300 ${expandedId === scooter.id ? 'rotate-180' : ''}`}>
                       <ChevronDown size={20} className="text-[#1E1E1C]" />
                    </div>
                  </div>

	
                  <AnimatePresence>
                    {expandedId === scooter.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="bg-[#F9F7F2]"
                      >
                        <div className="p-6">
                          <p className="text-sm font-bold text-[#7A6F60] uppercase mb-3 tracking-widest">Key Specs</p>
                          <ul className="space-y-2 mb-6">
                            {scooter.specs.map((spec, i) => (
                              <li key={i} className="flex items-center gap-2 text-[#1E1E1C] font-medium">
                                <Golf size={16} />
                                {spec}
                              </li>
                            ))}
                          </ul>
                          <Button 
                            variant="primary" 
                            className="w-full py-2 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/scooters');
                            }}
                          >
                            Read More
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
             ))}
          </div>
        </div>
      </section>
    </div>
   </>
  );
};
  

export default HomePage;